import { useCallback, useEffect, useRef, useState } from "react";
import { HiloConversacion } from "./HiloConversacion";
import { fechaCorta } from "../fechaCorta";
import { MensajesGraficos } from "./MensajesGraficos";
import { edadDias, estadoDe, paginasVisibles, textoEdad } from "./mensajesUtil";
import { fetchSocialMensajes, fetchSocialMensajesLista, fetchSocialMensajesPanorama } from "../../../api";
import type {
  SocialMensajesConversacion,
  SocialMensajesFiltro,
  SocialMensajesListaPayload,
  SocialMensajesPanorama,
  SocialMensajesPayload,
} from "../../../types";
import type { SandboxTheme } from "../sandbox/SandboxResumen";
import "./mensajes.css";

const PLATAFORMAS = [
  { id: "messenger", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
];

const INBOX_META = "https://business.facebook.com/latest/inbox/all";

const num = (v: number) => v.toLocaleString("es-CL");

type Conteo = keyof SocialMensajesPanorama["conteos"];

const FILTROS: Array<{ id: SocialMensajesFiltro; label: string; conteo: Conteo }> = [
  { id: "pendientes", label: "Pendientes", conteo: "pendientes" },
  { id: "para_responder", label: "Para responder", conteo: "para_responder" },
  { id: "leads_pendientes", label: "Posibles leads", conteo: "leads" },
  { id: "vencidas", label: "Vencidas", conteo: "vencidas" },
  { id: "respondidas", label: "Respondidas", conteo: "respondidas" },
  { id: "todas", label: "Todas", conteo: "total" },
];

function Etiquetas({ c, diasParaResponder }: { c: SocialMensajesConversacion; diasParaResponder: number }) {
  const estado = estadoDe(c, diasParaResponder);
  return (
    <>
      {estado === "respondida" && <span className="msj-tag ok">Respondida</span>}
      {estado === "responder" && <span className="msj-tag now">Para responder</span>}
      {estado === "vencida" && <span className="msj-tag">Vencida</span>}
      {c.es_posible_lead && <span className="msj-tag lead">Posible lead</span>}
      {c.es_informativa && !c.es_posible_lead && <span className="msj-tag">Informativa</span>}
    </>
  );
}

export function SocialMensajesTab({ clientSlug, theme = "dark" }: { clientSlug: string; theme?: SandboxTheme }) {
  const [platform, setPlatform] = useState("messenger");
  const [resumen, setResumen] = useState<SocialMensajesPayload | null>(null);
  const [panorama, setPanorama] = useState<SocialMensajesPanorama | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState<SocialMensajesFiltro>("pendientes");
  const [page, setPage] = useState(1);
  const [lista, setLista] = useState<SocialMensajesListaPayload | null>(null);
  const [listaError, setListaError] = useState<string | null>(null);
  const [listaCargando, setListaCargando] = useState(false);
  const [intento, setIntento] = useState(0);
  const [sel, setSel] = useState<string | null>(null);
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [ir, setIr] = useState("");
  const panel = useRef<HTMLElement>(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    Promise.all([fetchSocialMensajes(clientSlug), fetchSocialMensajesPanorama(clientSlug, platform)])
      .then(([r, p]) => {
        setResumen(r);
        setPanorama(p);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Error desconocido"))
      .finally(() => setCargando(false));
  }, [clientSlug, platform]);

  useEffect(cargar, [cargar]);

  useEffect(() => {
    setFiltro("pendientes");
    setPage(1);
    setSel(null);
  }, [clientSlug, platform]);

  useEffect(() => {
    let activo = true;
    setListaCargando(true);
    setListaError(null);
    fetchSocialMensajesLista(clientSlug, platform, filtro, page)
      .then((r) => {
        if (!activo) return;
        setLista(r);
        setSel((actual) => (actual && r.items.some((i) => i.conversation_id === actual) ? actual : (r.items[0]?.conversation_id ?? null)));
      })
      .catch((e: unknown) => activo && setListaError(e instanceof Error ? e.message : "Error desconocido"))
      .finally(() => activo && setListaCargando(false));
    return () => {
      activo = false;
    };
  }, [clientSlug, platform, filtro, page, intento]);

  const elegirFiltro = (f: SocialMensajesFiltro) => {
    setFiltro(f);
    setPage(1);
    setSel(null);
    setDetalleAbierto(false);
  };
  const irAPanel = (f: SocialMensajesFiltro) => {
    elegirFiltro(f);
    panel.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const fila = resumen?.plataformas.find((p) => p.platform === platform);
  const k = panorama?.conteos;
  const u = panorama?.umbrales;
  const paginas = lista ? Math.max(1, Math.ceil(lista.total / lista.page_size)) : 1;
  const desde = lista && lista.total > 0 ? (lista.page - 1) * lista.page_size + 1 : 0;
  const hasta = lista ? Math.min(lista.total, lista.page * lista.page_size) : 0;
  const seleccionada = lista?.items.find((i) => i.conversation_id === sel) ?? null;

  const encabezado = (
    <header className="msj-head">
      <div className="msj-head-t">
        <h1>Mensajes</h1>
        {fila && (
          <p className="msj-sub">
            {PLATAFORMAS.find((p) => p.id === platform)?.label} · {num(fila.conversaciones ?? 0)} conversaciones · desde {fechaCorta(fila.primera_fecha)} hasta {fechaCorta(fila.ultima_fecha)}
          </p>
        )}
      </div>
      <div className="msj-seg" role="group" aria-label="Plataforma">
        {PLATAFORMAS.map((p) => (
          <button key={p.id} type="button" className={p.id === "instagram" ? "ig" : "fb"} aria-pressed={platform === p.id} onClick={() => setPlatform(p.id)}>
            {p.label}
          </button>
        ))}
      </div>
    </header>
  );

  const raiz = (children: React.ReactNode) => (
    <div className="sbx msj" data-theme={theme}>
      {encabezado}
      {children}
    </div>
  );

  if (cargando) return raiz(<div className="msj-vacio" role="status">Cargando mensajes…</div>);
  if (error)
    return raiz(
      <div className="msj-vacio" role="alert">
        <p>No se pudieron cargar los mensajes: {error}</p>
        <button type="button" className="msj-btn" onClick={cargar}>Reintentar</button>
      </div>,
    );
  if (!fila || !panorama || !k || !u)
    return raiz(
      <div className="msj-vacio">Sin datos de mensajes de {PLATAFORMAS.find((p) => p.id === platform)?.label ?? platform} para este cliente.</div>,
    );

  return raiz(
    <>
      <section aria-label="Qué atender primero">
        <div className="msj-sec-h">
          <h2>Qué atender primero</h2>
        </div>
        <div className="msj-todo">
          <article className="msj-todo-card">
            <span className="pri hot">Urgente</span>
            <div className="n num">{num(k.leads)}</div>
            <h3>Posibles leads sin respuesta</h3>
            <p>
              {num(k.leads_recientes)} de hasta {u.vencida} días y {num(k.leads - k.leads_recientes)} de más de {u.vencida}. Los antiguos probablemente ya se perdieron; revisa primero los recientes.
            </p>
            <button type="button" className="msj-btn primary" disabled={k.leads === 0} onClick={() => irAPanel("leads_pendientes")}>Ver leads</button>
          </article>
          <article className="msj-todo-card">
            <span className="pri mid">Esta semana</span>
            <div className="n num">{num(k.para_responder)}</div>
            <h3>Conversaciones recientes por responder</h3>
            <p>Escribieron en los últimos {u.responder} días y la última palabra la tiene el cliente.</p>
            <button type="button" className="msj-btn primary" disabled={k.para_responder === 0} onClick={() => irAPanel("para_responder")}>Responder ahora</button>
          </article>
          <article className="msj-todo-card">
            <span className="pri low">Vencidas</span>
            <div className="n num">{num(k.mas_vencida)}</div>
            <h3>Conversaciones de más de {u.vencida} días</h3>
            <p>Son {k.pendientes > 0 ? Math.round((k.mas_vencida / k.pendientes) * 100) : 0}% de lo pendiente y esconden lo que sí se puede atender.</p>
            <button type="button" className="msj-btn" disabled={k.vencidas === 0} onClick={() => irAPanel("vencidas")}>Ver vencidas</button>
          </article>
        </div>
      </section>

      <section className="msj-kpis" aria-label="Indicadores">
        {[
          ["Pendientes", k.pendientes, k.total > 0 ? `${Math.round((k.pendientes / k.total) * 100)}% del total` : ""],
          ["Para responder", k.para_responder, `≤ ${u.responder} días`],
          ["Vencidas", k.vencidas, `> ${u.responder} días`],
          ["Posibles leads", k.leads, "sin respuesta"],
          ["Respondidas", k.respondidas, `de ${num(k.total)}`],
        ].map(([l, v, s]) => (
          <div key={String(l)} className="msj-kpi">
            <div className="l">{l}</div>
            <div className="v num">{num(Number(v))}<small>{s}</small></div>
          </div>
        ))}
      </section>

      <section ref={panel} className={`msj-panel${detalleAbierto ? " detail-open" : ""}`} aria-label="Conversaciones" aria-busy={listaCargando}>
        <div className="msj-list">
          <div className="msj-chips" role="group" aria-label="Filtros">
            {FILTROS.map((f) => (
              <button key={f.id} type="button" className="msj-chip" aria-pressed={filtro === f.id} onClick={() => elegirFiltro(f.id)}>
                {f.label}<span className="c num">{num(k[f.conteo])}</span>
              </button>
            ))}
          </div>

          <div className="msj-rows">
            {listaError ? (
              <div className="msj-vacio" role="alert">
                <p>No se pudo cargar la lista: {listaError}</p>
                <button type="button" className="msj-btn" onClick={() => setIntento((x) => x + 1)}>Reintentar</button>
              </div>
            ) : !lista || lista.items.length === 0 ? (
              <div className="msj-vacio">{listaCargando ? "Cargando conversaciones…" : "No hay conversaciones en este filtro."}</div>
            ) : (
              lista.items.map((c) => {
                const estado = estadoDe(c, u.responder);
                return (
                  <button
                    key={c.conversation_id}
                    type="button"
                    className={`msj-row${c.es_posible_lead && estado !== "respondida" ? " lead" : estado === "responder" ? " now" : ""}`}
                    aria-current={sel === c.conversation_id}
                    onClick={() => {
                      setSel(c.conversation_id);
                      setDetalleAbierto(true);
                    }}
                  >
                    <span className="t">{c.ultimo_texto_user?.trim() || "Sin texto"}</span>
                    <span className="m">
                      <Etiquetas c={c} diasParaResponder={u.responder} />
                      <span>{textoEdad(edadDias(c.ultimo_mensaje_ts))}</span>
                      <span aria-hidden="true">·</span>
                      <span className="num">{num((c.mensajes_user ?? 0) + (c.mensajes_page ?? 0))} mensajes</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {lista && lista.total > 0 && (
            <nav className="msj-pager" aria-label="Paginación">
              <span className="count num">{num(desde)}–{num(hasta)} de {num(lista.total)}</span>
              <div className="pages">
                <button type="button" className="pg" disabled={page <= 1 || listaCargando} aria-label="Primera página" onClick={() => setPage(1)}>«</button>
                <button type="button" className="pg" disabled={page <= 1 || listaCargando} aria-label="Página anterior" onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
                {paginasVisibles(page, paginas).map((p, i) =>
                  p === "…" ? (
                    <span key={`g${i}`} className="gap">…</span>
                  ) : (
                    <button key={p} type="button" className="pg num" aria-current={p === page ? "page" : undefined} disabled={listaCargando} onClick={() => setPage(p)}>
                      {p}
                    </button>
                  ),
                )}
                <button type="button" className="pg" disabled={page >= paginas || listaCargando} aria-label="Página siguiente" onClick={() => setPage((p) => p + 1)}>›</button>
                <button type="button" className="pg" disabled={page >= paginas || listaCargando} aria-label="Última página" onClick={() => setPage(paginas)}>»</button>
              </div>
              <label className="goto">
                Ir a
                <input
                  type="number"
                  min={1}
                  max={paginas}
                  inputMode="numeric"
                  aria-label="Número de página"
                  placeholder={String(page)}
                  value={ir}
                  onChange={(e) => setIr(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    setPage(Math.min(paginas, Math.max(1, parseInt(ir, 10) || 1)));
                    setIr("");
                  }}
                />
                de {num(paginas)}
              </label>
            </nav>
          )}
        </div>

        <div className="msj-detail">
          {seleccionada ? (
            <>
              <div className="msj-d-head">
                <button type="button" className="msj-btn msj-back" onClick={() => setDetalleAbierto(false)}>← Volver</button>
                <div className="who">
                  <h2>Conversación {PLATAFORMAS.find((p) => p.id === platform)?.label}</h2>
                  <div className="tags">
                    <Etiquetas c={seleccionada} diasParaResponder={u.responder} />
                    <span className="msj-tag num">{textoEdad(edadDias(seleccionada.ultimo_mensaje_ts))}</span>
                  </div>
                </div>
              </div>
              <div className="msj-thread">
                <HiloConversacion clientSlug={clientSlug} platform={platform} conversationId={seleccionada.conversation_id} />
              </div>
              <div className="msj-d-foot">
                <span>Solo lectura. Para responder, abre la conversación en Meta.</span>
                <a href={INBOX_META} target="_blank" rel="noopener noreferrer">Abrir en Meta Business Suite ↗</a>
              </div>
            </>
          ) : (
            <div className="msj-vacio">Elige una conversación de la lista para verla aquí.</div>
          )}
        </div>
      </section>

      <MensajesGraficos panorama={panorama} />
    </>,
  );
}
